import { Checkbox } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { Accordion } from "@aviary";
import type { Filter } from "@shared/components/CatalogSearch/CatalogSearch";
import { usePopulationsSharedQuery } from "@shared/data/queries/PopulationQuery/Population.query.generated";
import { FilterType } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { l } from "@shared/locales/i18n";
import type * as Types from "@shared/types/graphqlGenerated";

import { FilterTitle } from "../shared/FilterTitle/FilterTitle";

import * as styles from "./Demographics.styles";

type Demographic = Pick<Types.PopulationContextualFilters, "id" | "esResultCount"> & {
  population: Pick<Types.Population, "id" | "name">;
};
type NonContextualDemographic = Pick<Types.Population, "id" | "name">;

interface Props {
  isBold?: boolean;
  logFilterEvent: (entityName: string, entityType: string) => void;
  isContextual?: boolean;
  contextualData?: Demographic[];
}

const Demographics = ({ isBold, logFilterEvent, isContextual = false, contextualData }: Props) => {
  const { t } = useTranslation();
  const { data, loading } = usePopulationsSharedQuery({
    variables: {
      input: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
    },
    skip: isContextual,
  });
  const { activeFilters, setActiveFilters } = useCatalogSearch();

  const demographicFilters: Demographic[] = contextualData ?? [];
  const nonContextualDemographicFilters: NonContextualDemographic[] =
    data?.viewer?.populations ?? [];

  if (isContextual ? (contextualData?.length ?? 0) === 0 : loading || !data?.viewer?.populations) {
    return null;
  }

  // new contextual filters that include result count
  const renderDemographicLabel = (demographicFilterWithCount: Demographic) => {
    return `${demographicFilterWithCount.population.name} (${demographicFilterWithCount.esResultCount})`;
  };

  const renderDemographics = () =>
    demographicFilters.map((demoWithCount: Demographic) => {
      return (
        <span key={demoWithCount.population.id} css={styles.checkbox}>
          <Checkbox
            isFullWidth
            checked={isDemographicActive(demoWithCount.population.id)}
            onChange={() =>
              handleDemographicSelect({
                name: demoWithCount.population.name,
                id: demoWithCount.population.id,
                category: "Demographic",
              })
            }
          >
            {renderDemographicLabel(demoWithCount)}
          </Checkbox>
        </span>
      );
    });

  // old filters that don't include result count
  const renderNonContextualDemographicLabel = (demographicFilter: NonContextualDemographic) => {
    return demographicFilter.name;
  };

  const renderNonContextualDemographics = () =>
    nonContextualDemographicFilters.map((demo: NonContextualDemographic) => {
      return (
        <span key={demo.id} css={styles.checkbox}>
          <Checkbox
            isFullWidth
            checked={isDemographicActive(demo.id)}
            onChange={() =>
              handleDemographicSelect({
                name: demo.name,
                id: demo.id,
                category: "Demographic",
              })
            }
          >
            {renderNonContextualDemographicLabel(demo)}
          </Checkbox>
        </span>
      );
    });

  // shared logic for both contextual and non-contextual filters
  const isDemographicActive = (id: string): boolean =>
    activeFilters.some(activeDemo => activeDemo.id === id);

  const handleDemographicSelect = (selectedDemo: Filter) => {
    const distanceFromTop = window?.scrollY;
    if (isDemographicActive(selectedDemo.id)) {
      setActiveFilters(activeFilters.filter(demo => demo.id !== selectedDemo.id));
    } else {
      logFilterEvent(selectedDemo.name, FilterType.DEMOGRAPHIC);
      setActiveFilters([...activeFilters, selectedDemo]);
    }
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const activeDemographicCount = activeFilters.filter(
    active => active?.category === "Demographic"
  )?.length;

  return (
    <Accordion
      coreTriggerContent={
        <FilterTitle
          title={t(l.sharedCatalog.filter.Demographics)}
          count={activeDemographicCount}
          isSecondaryWeight={isBold}
          isBadge
        />
      }
    >
      <div css={styles.checkboxList}>
        {isContextual ? renderDemographics() : renderNonContextualDemographics()}
      </div>
    </Accordion>
  );
};

export { Demographics };
